import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CoverageMap from '@components/Maps/CoverageMap';
import ButtonHelp from '@components/Maps/ButtonHelp';
import useSearchParams from '@hooks/useSearchParams';
import CoverageMapImg from '@assets/images/coverage-map-help.png';
import CustomCard from '@components/CustomCard';

import * as S from './styles';

const ZOOM_DEFAULT = 4;

const Maps = () => {
  const { t } = useTranslation('maps');

  const [search, setSearch] = useState();

  const { setSearchParams, clearSearchParams, searchParams } = useSearchParams({
    type: 'pagination',
    defaultParams: ['lat', 'lng', 'zoom'],
  });

  const { lat, lng, zoom } = useMemo(() => searchParams, [searchParams]);

  const mapsCenter = useMemo(
    () => lat && lng && { lat: Number(lat), lng: Number(lng) },
    [lat, lng],
  );

  useEffect(() => {
    if ((lat && !lng) || (!lat && lng)) {
      clearSearchParams(['lat', 'zoom']);
    }
  }, [lat, lng, clearSearchParams]);

  const handleSetZoomParam = useCallback(
    (value: any) => {
      setSearchParams({ zoom: value });
    },
    [setSearchParams],
  );

  const handleSetCenterParams = useCallback(
    (values: any) => {
      setSearchParams({ lat: values?.lat, lng: values?.lng });
    },

    [setSearchParams],
  );

  const handleClearLocation = useCallback(() => {
    setSearchParams({ zoom: ZOOM_DEFAULT });
  }, [setSearchParams]);

  const mapToBeRendered = useMemo(
    () => ({
      title: t('coverage-map'),
      imgHelp: CoverageMapImg,
      description: t('help.description.coverage'),
      map: (
        <CoverageMap
          allowSearch
          allowCoverage
          mapHeight="96%"
          mapsZoom={zoom ? Number(zoom) : ZOOM_DEFAULT}
          setMapsZoom={handleSetZoomParam}
          mapsCenter={mapsCenter}
          setMapsCenter={handleSetCenterParams}
          searchValue={search}
          setSearchValue={setSearch}
          setSearchParams={setSearchParams}
          clearLocation={handleClearLocation}
        />
      ),
    }),
    [
      t,
      zoom,
      handleSetZoomParam,
      mapsCenter,
      handleSetCenterParams,
      search,
      setSearchParams,
      handleClearLocation,
    ],
  );

  return (
    <S.Container>
      <CustomCard
        title={mapToBeRendered?.title}
        style={{ height: '100%' }}
        bodyStyle={{ padding: 0 }}
      >
        {mapToBeRendered?.map}
        <ButtonHelp
          type="help"
          img={mapToBeRendered?.imgHelp}
          description={mapToBeRendered?.description}
        />
      </CustomCard>
    </S.Container>
  );
};

export default Maps;
